import Fuse from 'fuse.js';
import type { Landmark } from '../types/landmark';
import type { Business } from '../types/business';
import type { Place } from '../types/place';

export type SearchKind = 'landmark' | 'business' | 'place';

export interface SearchResult {
  kind: SearchKind;
  id: string;
  name: string;
  /** Secondary line — period for landmarks, address for businesses, kind/parent for places. */
  subtitle: string;
  coordinates: [number, number];
  /** Suggested camera zoom when flying to this result (places carry their own; others use defaults). */
  flyZoom?: number;
  /** Original record — caller decides what to do with it. */
  record: Landmark | Business | Place;
}

interface IndexedLandmark {
  kind: 'landmark';
  id: string;
  name: string;
  alternativeNames: string;
  period: string;
  type: string;
  summary: string;
  coordinates: [number, number];
  record: Landmark;
}

interface IndexedBusiness {
  kind: 'business';
  id: string;
  name: string;
  tagline: string;
  address: string;
  description: string;
  category: string;
  coordinates: [number, number];
  record: Business;
}

interface IndexedPlace {
  kind: 'place';
  id: string;
  name: string;
  alternativeNames: string;
  parent: string;
  placeKind: string;
  coordinates: [number, number];
  record: Place;
}

type IndexedItem = IndexedLandmark | IndexedBusiness | IndexedPlace;

const FUSE_OPTIONS: ConstructorParameters<typeof Fuse<IndexedItem>>[1] = {
  // Lower threshold = stricter match. 0.4 catches typos but rejects unrelated terms.
  threshold: 0.4,
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: 2,
  // Multi-key weighted search — name carries the most weight.
  keys: [
    { name: 'name', weight: 0.5 },
    { name: 'alternativeNames', weight: 0.25 },
    { name: 'period', weight: 0.05 },
    { name: 'type', weight: 0.05 },
    { name: 'tagline', weight: 0.1 },
    { name: 'address', weight: 0.05 },
    { name: 'category', weight: 0.05 },
    { name: 'description', weight: 0.05 },
    { name: 'summary', weight: 0.05 },
    { name: 'parent', weight: 0.1 },
    { name: 'placeKind', weight: 0.02 },
  ],
};

export class SearchIndex {
  private readonly fuse: Fuse<IndexedItem>;
  private readonly count: { landmarks: number; businesses: number; places: number };

  constructor(landmarks: Landmark[], businesses: Business[], places: Place[] = []) {
    const items: IndexedItem[] = [
      ...landmarks.map(landmarkToIndexed),
      ...businesses.map(businessToIndexed),
      ...places.map(placeToIndexed),
    ];
    this.fuse = new Fuse(items, FUSE_OPTIONS);
    this.count = {
      landmarks: landmarks.length,
      businesses: businesses.length,
      places: places.length,
    };
  }

  /**
   * Search across all landmarks + businesses + places. Returns up to `limit`
   * results, ranked by Fuse's score (lower = better). Empty query returns
   * no results.
   *
   * Places get a stronger boost than featured landmarks — typing "Bruges"
   * should always surface the city before any heritage object inside it.
   * Featured-tier landmarks are mildly boosted over baseline.
   */
  search(query: string, limit: number = 10): SearchResult[] {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    const raw = this.fuse.search(trimmed, { limit: limit * 2 });

    const ranked = raw.map((hit) => {
      const item = hit.item;
      const baseScore = hit.score ?? 1;
      let boost = 0;
      if (item.kind === 'place') boost = -0.15;
      else if (item.kind === 'landmark' && item.record.tier === 'featured') boost = -0.05;
      return { hit, adjusted: baseScore + boost };
    });
    ranked.sort((a, b) => a.adjusted - b.adjusted);

    return ranked.slice(0, limit).map(({ hit }) => indexedToResult(hit.item));
  }

  /** Diagnostics — useful for tests + the empty-state hint. */
  size(): { total: number; landmarks: number; businesses: number; places: number } {
    return {
      total: this.count.landmarks + this.count.businesses + this.count.places,
      ...this.count,
    };
  }
}

function landmarkToIndexed(landmark: Landmark): IndexedLandmark {
  return {
    kind: 'landmark',
    id: landmark.id,
    name: landmark.name,
    alternativeNames: (landmark.alternativeNames ?? []).join(' '),
    period: landmark.period ?? '',
    type: landmark.type ?? '',
    summary: landmark.tier === 'featured' ? landmark.wikipediaSummary ?? '' : '',
    coordinates: landmark.coordinates,
    record: landmark,
  };
}

function businessToIndexed(business: Business): IndexedBusiness {
  return {
    kind: 'business',
    id: business.id,
    name: business.name,
    tagline: business.tagline ?? '',
    address: business.address,
    description: business.description,
    category: business.category,
    coordinates: business.coordinates,
    record: business,
  };
}

function placeToIndexed(place: Place): IndexedPlace {
  return {
    kind: 'place',
    id: place.id,
    name: place.name,
    alternativeNames: (place.altNames ?? []).join(' '),
    parent: place.parent ?? '',
    placeKind: place.kind,
    coordinates: place.coordinates,
    record: place,
  };
}

function indexedToResult(item: IndexedItem): SearchResult {
  if (item.kind === 'landmark') {
    const subtitle = [item.record.period, item.record.type].filter(Boolean).join(' · ');
    return {
      kind: 'landmark',
      id: item.id,
      name: item.name,
      subtitle: subtitle.length > 0 ? subtitle : 'Heritage record',
      coordinates: item.coordinates,
      record: item.record,
    };
  }
  if (item.kind === 'business') {
    return {
      kind: 'business',
      id: item.id,
      name: item.name,
      subtitle: item.record.tagline ?? item.record.address,
      coordinates: item.coordinates,
      record: item.record,
    };
  }
  const place = item.record;
  const kindLabel = place.kind === 'district' ? 'District' : place.kind === 'city' ? 'City' : 'Town';
  const subtitle = place.parent ? `${kindLabel} of ${place.parent}` : kindLabel;
  return {
    kind: 'place',
    id: item.id,
    name: item.name,
    subtitle,
    coordinates: item.coordinates,
    flyZoom: place.flyZoom,
    record: place,
  };
}
